import { normaliseFormsBaseUrl, type FormSource, type FormType } from './forms.js';

/**
 * Ways to put a hosted form on another page: the auto-height iframe (Wix HTML
 * embed), a fixed-height iframe (editors that strip <script>, e.g. nopCommerce)
 * and a plain link button. Shared by the builder's Embed dialog and the admin
 * "Public form" page so the snippets never drift apart.
 */

/** Escape text for an HTML attribute or element body. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** The public address of a form: `<base>/f/<slug>`. */
export function publicFormUrl(baseUrl: string, slug: string): string {
  return `${normaliseFormsBaseUrl(baseUrl)}/f/${encodeURIComponent(slug)}`;
}

export interface EmbedSnippets {
  url: string;
  autoHeight: string;
  fixedHeight: string;
  link: string;
}

/** Brand green (tokens.css --green-900); inline because it lands on Wix pages. */
const BUTTON_STYLE =
  'display:inline-block;padding:12px 22px;background:#154734;color:#ffffff;' +
  'border-radius:8px;text-decoration:none;font-weight:600;font-family:inherit';

export function embedSnippets(
  baseUrl: string,
  slug: string,
  name: string,
  linkText = name,
): EmbedSnippets {
  const url = publicFormUrl(baseUrl, slug);
  const src = escapeHtml(url);
  const title = escapeHtml(name);
  // The element id goes inside a JS string too, so keep it to safe characters.
  const id = `jfh-form-${slug.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const autoHeight = [
    '<iframe',
    `  id="${id}"`,
    `  src="${src}"`,
    '  style="width:100%;border:0;display:block"',
    `  title="${title}"></iframe>`,
    '<script>',
    '  window.addEventListener("message", function (e) {',
    '    if (!e.data || e.data.type !== "jfh-form-resize") return;',
    `    var f = document.getElementById("${id}");`,
    '    if (f && typeof e.data.height === "number") f.style.height = e.data.height + "px";',
    '  });',
    '</script>',
  ].join('\n');
  const fixedHeight = [
    '<iframe',
    `  src="${src}"`,
    '  style="width:100%;height:900px;border:0;display:block"',
    `  title="${title}"></iframe>`,
  ].join('\n');
  const link = [
    `<a href="${src}" target="_blank" rel="noopener"`,
    `  style="${BUTTON_STYLE}">${escapeHtml(linkText)}</a>`,
  ].join('\n');
  return { url, autoHeight, fixedHeight, link };
}

/** GET /api/admin/public-forms — one row per form, for the "Public form" page. */
export interface PublicFormRow {
  id: string;
  slug: string;
  name: string;
  type: FormType;
  folderId: string | null;
  source: FormSource;
  /** Published, not archived, with a current version: /f/<slug> serves it. */
  live: boolean;
  /** Why it isn't live (null when live). */
  notLiveReason: 'draft' | 'archived' | null;
  version: number | null;
  publishedAt: string | null;
  publishedBy: string | null;
  archivedAt: string | null;
  submissions: { last7: number; last30: number; total: number; lastAt: string | null };
}

export interface PublicFormsResponse {
  baseUrl: string;
  forms: PublicFormRow[];
}
